import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
    selectMatrixIdToUserId,
    selectUserIdToMatrixId,
} from '../store/features/IdResolver/idResolverSlice';
import { UserStatus } from '../store/features/Team/teamService';
import { useStatusSubscription } from './useStatusSubscription';

interface UseIdResolverResult {
    getUserId: (matrixId: string) => string;
    getMatrixId: (userId: string) => string;
    getStatus: (userId: string) => UserStatus | undefined;
    isLoading: boolean;
    isError: boolean;
    allStatuses: UserStatus[];
}

export const useIdResolver = (): UseIdResolverResult => {
    const matrixIdToUserId = useSelector(selectMatrixIdToUserId);
    const userIdToMatrixId = useSelector(selectUserIdToMatrixId);

    const allUserIds = useMemo(() => {
        const resolvedUserIds = Object.values(matrixIdToUserId).filter(id => id && id !== '');
        return [...new Set(resolvedUserIds)];
    }, [matrixIdToUserId]);

    const { statuses: allStatuses, getStatus: getStatusFromHook, isLoading: isLoadingStatuses } = useStatusSubscription({
        userIds: allUserIds
    });

    const getUserId = useCallback((matrixId: string): string => {
        if (!matrixId) return '';
        return matrixIdToUserId[matrixId] || '';
    }, [matrixIdToUserId]);

    const getMatrixId = useCallback((userId: string): string => {
        if (!userId) return '';
        return userIdToMatrixId[userId] || '';
    }, [userIdToMatrixId]);

    const getStatus = useCallback((userId: string): UserStatus | undefined => {
        return getStatusFromHook(userId);
    }, [getStatusFromHook]);

    return {
        getUserId,
        getMatrixId,
        getStatus,
        isLoading: isLoadingStatuses,
        isError: false,
        allStatuses
    };
};

export default useIdResolver;
